import { Injectable } from '@angular/core';
import { BehaviorSubject, catchError, distinctUntilChanged, map, Observable, of, Subject, switchMap, tap } from 'rxjs';
import { IFilterModel } from 'src/app/shared/models/filter-model';
import { DownloadUtil } from 'src/app/shared/utils/download-util';
import { InvoiceFilterAdapter } from '../../data/adapters/invoice-filter.adapter';
import { Invoice } from '../../domain/entities/invoice.entity';
import { InvoiceAction } from '../../domain/enums/invoice-action.enum';
import { INVOICE_STATUS_ACTION_CONFIG } from '../../domain/use-cases/invoice-status-action.config';
import { ExecuteInvoiceActionUseCase } from '../../domain/use-cases/execute-invoice-action.use-case';
import { ExportInvoicesCsvUseCase } from '../../domain/use-cases/export-invoices-csv.use-case';
import { GetInvoiceRevenueEditDataUseCase } from '../../domain/use-cases/get-invoice-revenue-edit-data.use-case';
import { GetInvoiceActionRulesUseCase } from '../../domain/use-cases/get-invoice-action-rules.use-case';
import { GetInvoiceSummaryUseCase } from '../../domain/use-cases/get-invoice-summary.use-case';
import { GetInvoicesUseCase } from '../../domain/use-cases/get-invoices.use-case';
import { UpdateInvoiceRevenueUseCase } from '../../domain/use-cases/update-invoice-revenue.use-case';
import { InvoicePageVm } from '../view-models/invoice-page.vm';
import { InvoiceStore, InvoiceStoreState } from '../store/invoice.store';
import { InvoiceExportRequest } from '../../domain/models/invoice-export.model';
import { InvoiceRevenueEditData, InvoiceRevenueEditRequest } from '../../domain/models/invoice-revenue-edit.model';

interface InvoiceLoadRequest {
  query: string;
  signature: string;
}

@Injectable()
export class InvoiceFacade {
  private readonly filterGroupId = 'invoiceCardFilter';
  private readonly filter = this.filterAdapter.createDefaultFilter();
  private readonly filterModelsSubject = new BehaviorSubject<readonly IFilterModel[]>(
    this.filterAdapter.createInitialFilterModels(this.filterGroupId)
  );
  private readonly loadSubject = new Subject<InvoiceLoadRequest>();
  private currentPage = 1;

  readonly filters$ = this.filterModelsSubject.asObservable();

  get currentFilters(): readonly IFilterModel[] {
    return this.filterModelsSubject.value;
  }

  readonly vm$: Observable<InvoicePageVm> = this.store.state$.pipe(
    map((state) => this.mapToVm(state))
  );

  constructor(
    private readonly store: InvoiceStore,
    private readonly filterAdapter: InvoiceFilterAdapter,
    private readonly getInvoices: GetInvoicesUseCase,
    private readonly getSummary: GetInvoiceSummaryUseCase,
    private readonly getActionRules: GetInvoiceActionRulesUseCase,
    private readonly executeInvoiceAction: ExecuteInvoiceActionUseCase,
    private readonly exportInvoicesCsv: ExportInvoicesCsvUseCase,
    private readonly getRevenueEditData: GetInvoiceRevenueEditDataUseCase,
    private readonly updateInvoiceRevenue: UpdateInvoiceRevenueUseCase
  ) {
    // Reactive loading: switchMap cancels in-flight requests on new filter emission
    this.loadSubject.pipe(
      distinctUntilChanged((previous, current) => previous.signature === current.signature),
      tap(() => this.store.setLoading()),
      switchMap(({ query }) =>
        this.getInvoices.execute(query).pipe(
          tap((list) => {
            this.store.setInvoices(list.items, list.total);
          }),
          catchError(() => {
            this.store.setError('Falha ao carregar notas fiscais');
            return of(null);
          })
        )
      )
    ).subscribe();
  }

  load(): void {
    this.currentPage = 1;
    this.filter.page(1);
    this.loadSubject.next(this.createLoadRequest());
  }

  loadMore(): void {
    this.currentPage++;
    this.filter.page(this.currentPage);
    this.store.setLoadingMore();

    this.getInvoices.execute(this.filter.convertFilterInQuery()).pipe(
      tap((list) => this.store.appendInvoices(list.items, list.total)),
      catchError(() => {
        this.store.setError('Falha ao carregar mais notas fiscais');
        return of(null);
      })
    ).subscribe();
  }

  search(text: string): void {
    this.filterAdapter.withSearch(this.filter, text);
    this.load();
  }

  filterBy(filters: readonly IFilterModel[]): void {
    const normalizedFilters = this.normalizeFilterModels(filters);
    this.filterModelsSubject.next(normalizedFilters);
    this.filterAdapter.withFilterModels(this.filter, normalizedFilters);
    this.load();
  }

  applyDefaultDateFilter(field: string): void {
    this.filterAdapter.withCurrentMonth(this.filter, field);
  }

  runAction(invoice: Invoice, action: InvoiceAction): void {
    this.store.setPendingAction(invoice.id, action);

    this.executeInvoiceAction.execute(invoice.revenueId, action, invoice.serviceInvoiceId).pipe(
      tap((result) => {
        this.store.clearPendingAction();

        if (result instanceof Blob) {
          DownloadUtil.download(result, `invoice-${invoice.id}.pdf`, 'application/pdf');
          return;
        }

        if (action !== InvoiceAction.DownloadDanfe) {
          this.load();
        }
      }),
      catchError(() => {
        this.store.clearPendingAction();
        const errorMsg = INVOICE_STATUS_ACTION_CONFIG[invoice.status].actions
          .find((item) => item.action === action)?.errorMessage ?? 'Falha ao executar ação';
        this.store.setError(errorMsg);
        return of(null);
      })
    ).subscribe();
  }

  getMonthTotal(month: number, year: number): Observable<number> {
    // Temporarily disabled: do not use the listing endpoint for summary/count indicators.
    return of(0);
  }

  exportCsv(request: InvoiceExportRequest): Observable<Blob> {
    return this.exportInvoicesCsv.execute(request);
  }

  getEditData(invoice: Invoice): Observable<InvoiceRevenueEditData> {
    return this.getRevenueEditData.execute(invoice.revenueId);
  }

  saveRevenueEdit(request: InvoiceRevenueEditRequest): Observable<InvoiceRevenueEditData> {
    return this.updateInvoiceRevenue.execute(request).pipe(
      tap((data) => this.store.updateRevenue(data))
    );
  }

  private mapToVm(state: InvoiceStoreState): InvoicePageVm {
    const pending = state.pendingAction;

    const cards = state.invoices.map((invoice) => ({
      invoice,
      statusRule: INVOICE_STATUS_ACTION_CONFIG[invoice.status],
      actions: this.getActionRules.execute(invoice),
      loadingAction: pending?.invoiceId === invoice.id ? pending.action : null,
    }));

    const pageState = state.loading
      ? 'loading'
      : state.errorMessage
      ? 'error'
      : cards.length === 0
      ? 'empty'
      : 'success';

    return {
      state: pageState,
      cards,
      summary: state.summary,
      total: state.total,
      hasMore: state.invoices.length < state.total,
      loadingMore: state.loadingMore,
      errorMessage: state.errorMessage,
    };
  }

  private createLoadRequest(): InvoiceLoadRequest {
    return {
      query: this.filter.convertFilterInQuery(),
      signature: this.createFilterSignature()
    };
  }

  private createFilterSignature(): string {
    const filters = [...this.filter.filters]
      .map((filter) => ({
        property: filter.property ?? '',
        fields: [...(filter.fields ?? [])].sort(),
        value: filter.value ?? null,
      }))
      .sort((a, b) => JSON.stringify(a).localeCompare(JSON.stringify(b)));

    return JSON.stringify(filters);
  }

  private normalizeFilterModels(filters: readonly IFilterModel[]): readonly IFilterModel[] {
    const normalized = new Map<string, IFilterModel>();

    filters.forEach((model) => {
      const normalizedKey = this.getNormalizedFilterKey(model);
      normalized.set(normalizedKey, {
        ...model,
        key: normalizedKey,
        filter: model.filter.map((item) => ({ ...item, key: normalizedKey }))
      });
    });

    return Array.from(normalized.values());
  }

  private getNormalizedFilterKey(model: IFilterModel): string {
    if (model.type === 'DATE' || model.type === 'MONTH' || model.key === 'month') {
      return 'date';
    }

    return model.key;
  }
}
